import { useEffect, useState } from 'react'
import {
  Database,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Users,
  Calendar,
  ChevronDown,
  ChevronRight,
  Bot,
  RefreshCw,
  Download,
  User,
} from 'lucide-react'
import { apiService } from '@/service/api'
import type {
  AdminDataResponse,
  ConversationSummary,
  FinetuningDataPoint,
} from '@/service/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { useTranslation } from '@/i18n'

export default function UserInteractionData() {
  const { t } = useTranslation()
  const [data, setData] = useState<AdminDataResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedConversations, setExpandedConversations] = useState<Set<string>>(new Set())
  const [feedbackFilter, setFeedbackFilter] = useState<'all' | 'positive' | 'negative'>('all')
  const [showFinetuning, setShowFinetuning] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.getAdminInteractionData(20, 50)
      setData(response)
    } catch (err) {
      console.error('Error loading interaction data:', err)
      setError(t('common:admin_stats.loading_error'))
    } finally {
      setLoading(false)
    }
  }

  const toggleConversation = (convId: string) => {
    setExpandedConversations((prev) => {
      const next = new Set(prev)
      if (next.has(convId)) {
        next.delete(convId)
      } else {
        next.add(convId)
      }
      return next
    })
  }

  // Count messages with reasoning paths
  const messagesWithReasoning = data?.recent_conversations.reduce((count, conv) => {
    return count + conv.messages.filter(msg => msg.reasoning_path && msg.reasoning_path.steps && msg.reasoning_path.steps.length > 0).length
  }, 0) || 0

  // Count messages with context
  const messagesWithContext = data?.recent_conversations.reduce((count, conv) => {
    return count + conv.messages.filter(msg => msg.context).length
  }, 0) || 0

  const exportFinetuningData = () => {
    if (!data?.finetuning_data) return

    const filteredData = data.finetuning_data.filter((item) => {
      if (feedbackFilter === 'positive') return item.feedback_value === true
      if (feedbackFilter === 'negative') return item.feedback_value === false
      return true
    })

    const jsonlData = filteredData
      .map((item) =>
        JSON.stringify({
          messages: [
            { role: 'user', content: item.user_message },
            { role: 'assistant', content: item.assistant_response },
          ],
          metadata: {
            feedback: item.feedback_value,
            feedback_message: item.feedback_message,
            agent: item.agent_used,
            board_name: item.context_name,
            board_type: item.context_type,
            document_context: item.context,
            reasoning_steps: item.reasoning_path?.steps?.length || 0,
            reasoning_duration: item.reasoning_path?.total_duration,
          },
          reasoning_path: item.reasoning_path,
          context: item.context,
        })
      )
      .join('\n')

    const blob = new Blob([jsonlData], { type: 'application/jsonl' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `finetuning_data_${new Date().toISOString().split('T')[0]}.jsonl`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            {t('common:admin_stats.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            {t('common:admin_stats.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={loadData} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('common:admin_stats.retry')}
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) return null

  const { database_stats: stats, context_memory_stats: contextStats, recent_conversations, finetuning_data } = data

  const hasAnyNewData = messagesWithReasoning > 0 || messagesWithContext > 0 ||
    data?.recent_conversations.some(conv => conv.context_name)

  return (
    <div className="space-y-6">
      {/* Info Banner for New Data Collection */}
      {!hasAnyNewData && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">{t('common:admin_stats.enhanced_data_banner_title')}</p>
          <p
            className="text-xs text-blue-700 dark:text-blue-400"
            dangerouslySetInnerHTML={{ __html: t('common:admin_stats.enhanced_data_banner_text') }}
          />
        </div>
      )}

      {/* Database Statistics Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              {t('common:admin_stats.database_stats_title')}
            </CardTitle>
            <CardDescription>{t('common:admin_stats.database_stats_description')}</CardDescription>
          </div>
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common:admin_stats.refresh')}
          </Button>
        </CardHeader>
        <CardContent>
          {/* Main Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <MessageSquare className="h-4 w-4" />
                <span className="text-sm font-medium">{t('common:admin_stats.conversations')}</span>
              </div>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{stats.total_conversations.toLocaleString()}</p>
            </div>

            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="flex items-center gap-2 text-purple-600 mb-1">
                <MessageSquare className="h-4 w-4" />
                <span className="text-sm font-medium">{t('common:admin_stats.total_messages')}</span>
              </div>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">{stats.total_messages.toLocaleString()}</p>
            </div>

            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <ThumbsUp className="h-4 w-4" />
                <span className="text-sm font-medium">{t('common:admin_stats.positive_feedback')}</span>
              </div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-400">{stats.messages_with_positive_feedback.toLocaleString()}</p>
            </div>

            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <ThumbsDown className="h-4 w-4" />
                <span className="text-sm font-medium">{t('common:admin_stats.negative_feedback')}</span>
              </div>
              <p className="text-2xl font-bold text-red-700 dark:text-red-400">{stats.messages_with_negative_feedback.toLocaleString()}</p>
            </div>
          </div>

          {/* Secondary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                <span className="text-xs font-medium">{t('common:admin_stats.unique_users')}</span>
              </div>
              <p className="text-lg font-semibold">{stats.unique_users}</p>
            </div>

            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                <span className="text-xs font-medium">{t('common:admin_stats.last_7_days')}</span>
              </div>
              <p className="text-lg font-semibold">{stats.conversations_last_7_days} {t('common:admin_stats.convs')}</p>
            </div>

            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                <span className="text-xs font-medium">{t('common:admin_stats.last_30_days')}</span>
              </div>
              <p className="text-lg font-semibold">{stats.conversations_last_30_days} {t('common:admin_stats.convs')}</p>
            </div>

            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <MessageSquare className="h-4 w-4" />
                <span className="text-xs font-medium">{t('common:admin_stats.user_assistant')}</span>
              </div>
              <p className="text-lg font-semibold">
                {stats.user_messages} / {stats.assistant_messages}
              </p>
            </div>
          </div>

          {/* New Data Collection Indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
            <div className="col-span-2 md:col-span-4 mb-2">
              <p className="text-sm font-semibold text-purple-900 dark:text-purple-300">{t('common:admin_stats.enhanced_data_collection')}</p>
              <p className="text-xs text-purple-700 dark:text-purple-400">{t('common:admin_stats.enhanced_data_description')}</p>
            </div>
            <div className="p-2 bg-white dark:bg-dark-card rounded">
              <p className="text-xs text-muted-foreground mb-1">{t('common:admin_stats.with_reasoning')}</p>
              <p className="text-lg font-semibold text-purple-700 dark:text-purple-400">{messagesWithReasoning}</p>
            </div>
            <div className="p-2 bg-white dark:bg-dark-card rounded">
              <p className="text-xs text-muted-foreground mb-1">{t('common:admin_stats.with_context')}</p>
              <p className="text-lg font-semibold text-amber-700 dark:text-amber-400">{messagesWithContext}</p>
            </div>
            <div className="p-2 bg-white dark:bg-dark-card rounded">
              <p className="text-xs text-muted-foreground mb-1">{t('common:admin_stats.with_charts')}</p>
              <p className="text-lg font-semibold text-blue-700 dark:text-blue-400">
                {data?.recent_conversations.reduce((count, conv) =>
                  count + conv.messages.filter(msg => msg.has_chart_data).length, 0
                )}
              </p>
            </div>
            <div className="p-2 bg-white dark:bg-dark-card rounded">
              <p className="text-xs text-muted-foreground mb-1">{t('common:admin_stats.board_names')}</p>
              <p className="text-lg font-semibold text-green-700 dark:text-green-400">
                {data?.recent_conversations.filter(conv => conv.context_name).length}
              </p>
            </div>
          </div>

          {/* Agent Breakdown */}
          {Object.keys(stats.active_agents_breakdown).length > 0 && (
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2 text-muted-foreground mb-3">
                <Bot className="h-4 w-4" />
                <span className="text-sm font-medium">{t('common:admin_stats.agent_usage')}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.active_agents_breakdown).map(([agent, count]) => (
                  <span
                    key={agent}
                    className="px-3 py-1 bg-white dark:bg-dark-card border dark:border-dark-border rounded-full text-sm"
                  >
                    <span className="font-medium">{agent}:</span> {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Context Memory Stats (if available) */}
      {contextStats && contextStats.total_contexts > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4" />
              {t('common:admin_stats.context_memory_title')}
            </CardTitle>
            <CardDescription>{t('common:admin_stats.context_memory_description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">{t('common:admin_stats.total_contexts')}</p>
                <p className="text-lg font-semibold">{contextStats.total_contexts}</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">{t('common:admin_stats.active_users')}</p>
                <p className="text-lg font-semibold">{contextStats.unique_users}</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">{t('common:admin_stats.last_7_days')}</p>
                <p className="text-lg font-semibold">{contextStats.contexts_last_7_days}</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">{t('common:admin_stats.equipment_mentions')}</p>
                <p className="text-lg font-semibold">{contextStats.equipment_mentioned_count}</p>
              </div>
            </div>

            {Object.keys(contextStats.question_categories).length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-muted-foreground mb-2">{t('common:admin_stats.question_categories')}</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(contextStats.question_categories).map(([category, count]) => (
                    <span
                      key={category}
                      className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded text-xs"
                    >
                      {category}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Finetuning Data Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setShowFinetuning(!showFinetuning)}
            >
              {showFinetuning ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {t('common:admin_stats.finetuning_data_title')} ({finetuning_data.length} {t('common:admin_stats.pairs')})
            </CardTitle>
            <CardDescription>{t('common:admin_stats.finetuning_data_description')}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button
                variant={feedbackFilter === 'all' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFeedbackFilter('all')}
              >
                {t('common:admin_stats.filter_all')}
              </Button>
              <Button
                variant={feedbackFilter === 'positive' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFeedbackFilter('positive')}
              >
                <ThumbsUp className="h-3 w-3 mr-1" />
                {t('common:admin_stats.filter_positive')}
              </Button>
              <Button
                variant={feedbackFilter === 'negative' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setFeedbackFilter('negative')}
              >
                <ThumbsDown className="h-3 w-3 mr-1" />
                {t('common:admin_stats.filter_negative')}
              </Button>
            </div>
            <Button onClick={exportFinetuningData} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              {t('common:admin_stats.export_jsonl')}
            </Button>
          </div>
        </CardHeader>

        {showFinetuning && (
          <CardContent>
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {finetuning_data
                .filter((item) => {
                  if (feedbackFilter === 'positive') return item.feedback_value === true
                  if (feedbackFilter === 'negative') return item.feedback_value === false
                  return true
                })
                .map((item, idx) => (
                  <FinetuningItem key={`${item.conversation_id}-${idx}`} item={item} />
                ))}
              {finetuning_data.length === 0 && (
                <p className="text-center text-muted-foreground py-8">{t('common:admin_stats.no_finetuning_data')}</p>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Recent Conversations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {t('common:admin_stats.recent_conversations_title')} ({recent_conversations.length})
          </CardTitle>
          <CardDescription>{t('common:admin_stats.recent_conversations_description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {recent_conversations.map((conv) => (
              <ConversationItem
                key={conv.conversation_id}
                conversation={conv}
                expanded={expandedConversations.has(conv.conversation_id)}
                onToggle={() => toggleConversation(conv.conversation_id)}
              />
            ))}
            {recent_conversations.length === 0 && (
              <p className="text-center text-muted-foreground py-8">{t('common:admin_stats.no_conversations')}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function FinetuningItem({ item }: { item: FinetuningDataPoint }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border dark:border-dark-border rounded-lg p-3 bg-white dark:bg-dark-card">
      <div
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {item.feedback_value === true && <ThumbsUp className="h-3 w-3 text-green-500" />}
            {item.feedback_value === false && <ThumbsDown className="h-3 w-3 text-red-500" />}
            {item.agent_used && (
              <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded">
                {item.agent_used}
              </span>
            )}
            {item.context_name && (
              <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                {item.context_name}
              </span>
            )}
            {item.reasoning_path && item.reasoning_path.steps && item.reasoning_path.steps.length > 0 && (
              <span className="text-xs px-2 py-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded border border-purple-200 dark:border-purple-800">
                {item.reasoning_path.steps.length} {t('common:admin_stats.steps')}
              </span>
            )}
            {item.context && (
              <span className="text-xs px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded border border-amber-200 dark:border-amber-800">
                {t('common:admin_stats.context')}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {new Date(item.timestamp).toLocaleString()}
            </span>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
            <span className="font-medium">{t('common:admin_stats.user')}:</span> {item.user_message}
          </p>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </div>

      {expanded && (
        <div className="mt-3 space-y-2">
          {/* Board/Workspace Info */}
          {(item.context_name || item.context_type) && (
            <div className="p-2 bg-slate-50 dark:bg-dark-muted rounded border border-slate-200 dark:border-dark-border">
              <p className="text-xs font-medium text-slate-700 dark:text-gray-300 mb-1">{t('common:admin_stats.board_context_info')}</p>
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {item.context_name && <p><span className="font-medium">{t('common:admin_stats.board')}:</span> {item.context_name}</p>}
                {item.context_type && <p><span className="font-medium">{t('common:admin_stats.type')}:</span> {item.context_type}</p>}
              </div>
            </div>
          )}

          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
            <p className="text-xs font-medium text-blue-700 dark:text-blue-400 mb-1">{t('common:admin_stats.user_message')}</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{item.user_message}</p>
          </div>

          <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
            <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">{t('common:admin_stats.assistant_response')}</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{item.assistant_response}</p>
          </div>

          {/* AI Reasoning Steps */}
          {item.reasoning_path && item.reasoning_path.steps && item.reasoning_path.steps.length > 0 && (
            <div className="p-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded">
              <p className="text-xs font-medium text-purple-700 dark:text-purple-400 mb-1 flex items-center gap-1">
                {t('common:admin_stats.ai_reasoning_steps')} ({item.reasoning_path.steps.length} {t('common:admin_stats.steps')}, {item.reasoning_path.total_duration || t('common:general.na')})
              </p>
              <div className="space-y-1">
                {item.reasoning_path.steps.map((step, idx) => (
                  <div key={idx} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-1">
                    <span className="font-medium text-purple-600">{step.step}.</span>
                    <span className="flex-1">{step.description}</span>
                    {step.completed && <span className="text-green-500">✓</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Document Context Used */}
          {item.context && (
            <div className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">{t('common:admin_stats.documents_context_used')}</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{item.context}</p>
            </div>
          )}

          {item.feedback_message && (
            <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
              <p className="text-xs font-medium text-yellow-700 dark:text-yellow-400 mb-1">{t('common:admin_stats.feedback_message')}</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{item.feedback_message}</p>
            </div>
          )}

          {(item.feedback_reporter_name || item.feedback_reporter_email || item.feedback_reporter_id) && (
            <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded">
              <p className="text-xs font-medium text-indigo-700 dark:text-indigo-400 mb-1 flex items-center gap-1">
                <User className="h-3 w-3" />
                {t('common:admin_stats.feedback_reporter')}
              </p>
              <div className="text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
                {item.feedback_reporter_name && (
                  <p><span className="font-medium">{t('common:admin_stats.name')}:</span> {item.feedback_reporter_name}</p>
                )}
                {item.feedback_reporter_email && (
                  <p><span className="font-medium">{t('common:admin_stats.email')}:</span> {item.feedback_reporter_email}</p>
                )}
                {item.feedback_reporter_id && !item.feedback_reporter_name && !item.feedback_reporter_email && (
                  <p><span className="font-medium">{t('common:admin_stats.user_id')}:</span> {item.feedback_reporter_id}</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ConversationItem({
  conversation,
  expanded,
  onToggle,
}: {
  conversation: ConversationSummary
  expanded: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="border dark:border-dark-border rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between p-3 bg-muted/50 cursor-pointer hover:bg-muted"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {conversation.message_count} {t('common:admin_stats.messages')}
              </span>
              {conversation.has_feedback && (
                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded">
                  {t('common:admin_stats.has_feedback')}
                </span>
              )}
              {conversation.active_agent && (
                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded">
                  {conversation.active_agent}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {conversation.context_type}
              {conversation.context_name && ` • ${conversation.context_name}`}
              {' • '}
              {new Date(conversation.created_at).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-3 space-y-2 bg-white dark:bg-dark-card">
          {conversation.messages.map((msg) => (
            <div
              key={msg.id}
              className={`p-2 rounded ${
                msg.role === 'user' ? 'bg-blue-50 dark:bg-blue-900/20 ml-0 mr-8' : 'bg-gray-50 dark:bg-dark-muted ml-8 mr-0'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-medium ${msg.role === 'user' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  {msg.role === 'user' ? t('common:admin_stats.user') : t('common:admin_stats.assistant')}
                </span>
                {msg.feedback_value === true && <ThumbsUp className="h-3 w-3 text-green-500" />}
                {msg.feedback_value === false && <ThumbsDown className="h-3 w-3 text-red-500" />}
                {msg.agent_used && (
                  <span className="text-xs text-purple-600">({msg.agent_used})</span>
                )}
                {msg.has_chart_data && (
                  <span className="text-xs text-blue-600" title={t('common:admin_stats.with_charts')}>📊</span>
                )}
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{msg.content}</p>

              {/* AI Reasoning Steps */}
              {msg.reasoning_path && msg.reasoning_path.steps && msg.reasoning_path.steps.length > 0 && (
                <div className="mt-2 p-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded">
                  <p className="text-xs font-medium text-purple-700 dark:text-purple-400 mb-1 flex items-center gap-1">
                    {t('common:admin_stats.ai_reasoning_steps')} ({msg.reasoning_path.steps.length} {t('common:admin_stats.steps')}, {msg.reasoning_path.total_duration || t('common:general.na')})
                  </p>
                  <div className="space-y-1">
                    {msg.reasoning_path.steps.map((step, idx) => (
                      <div key={idx} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-1">
                        <span className="font-medium text-purple-600">{step.step}.</span>
                        <span className="flex-1">{step.description}</span>
                        {step.completed && <span className="text-green-500">✓</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Document Context Used */}
              {msg.context && (
                <div className="mt-1 p-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    <span className="font-medium">{t('common:admin_stats.context')}:</span> {msg.context}
                  </p>
                </div>
              )}

              {msg.feedback_message && (
                <p className="text-xs text-orange-600 mt-1">{t('common:admin_stats.feedback')}: {msg.feedback_message}</p>
              )}
              {(msg.feedback_reporter_name || msg.feedback_reporter_email || msg.feedback_reporter_id) && (
                <div className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
                  <User className="h-3 w-3 flex-shrink-0" />
                  <span>
                    <span className="font-medium">{t('common:admin_stats.feedback_reporter')}:</span>
                    {' '}
                    {[msg.feedback_reporter_name, msg.feedback_reporter_email].filter(Boolean).join(' - ') || msg.feedback_reporter_id}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
